import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Columns3,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  HeartPulse,
  History,
  Info,
  Loader2,
  Play,
  Printer,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  Table2,
  Users,
} from "lucide-react";
import { getApiErrorDetail } from "../../../utils/idempotency";
import { toDisplayString } from "../../Bank/bankErrorUtils";
import { useEmployees } from "../../../hooks/useEmployees";
import ReportsEnterpriseShell from "../components/ReportsEnterpriseShell";
import { useReportsNavigation } from "../useReportsNavigation";
import { PAYROLL_HUB_REPORTS } from "../PayrollReportsWorkspace";
import {
  downloadEsiReport,
  getEsiHistory,
  loadFilterOptions,
  previewEsiReport,
  triggerBlobDownload,
  downloadArtifact,
} from "./esiReportsApi";
import { printRowsAsTable } from "../reportPrint";
import "../../../styles/payrollReportsWorkspace.css";
import "../../../styles/esiReports.css";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const PAGE_SIZE = 50;
const SORTABLE = new Set([
  "insurance_number",
  "employee_code",
  "employee_name",
  "gross_wages",
  "esi_eligible_wages",
  "employee_contribution",
  "employer_contribution",
  "total_contribution",
]);

const SUMMARY_CARDS = [
  { key: "total_employees", label: "Total Employees", money: false },
  { key: "eligible_employees", label: "Eligible Employees", money: false, tone: "ok" },
  { key: "excluded_employees", label: "Excluded Employees", money: false, tone: "warn" },
  { key: "total_gross_wages", label: "Total Gross Wages", money: true },
  { key: "total_esi_eligible_wages", label: "Total Eligible Wages", money: true },
  { key: "total_employee_contribution", label: "Employee Contribution", money: true },
  { key: "total_employer_contribution", label: "Employer Contribution", money: true },
  { key: "grand_total_contribution", label: "Grand Total Contribution", money: true },
  { key: "average_eligible_wage", label: "Average Eligible Wage", money: true },
];

function formatMoney(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function cellDisplay(row, col) {
  const raw = row[col.key];
  if (raw == null || raw === "") return col.type === "money" ? "" : "—";
  if (col.type === "money") return formatMoney(raw);
  return String(raw);
}

const nowYear = new Date().getFullYear();

/** One-line explanation shown under the tab bar for the selected tab. */
const TAB_INFO = {
  report:
    "Report: ESIC contribution per employee for the selected period (gross wages, eligible wages, employee and employer share) with totals, from finalized payroll.",
  validations:
    "Validations: problems that can make the ESIC upload fail or be wrong (e.g. missing insurance / IP number, wages above the ESI ceiling, zero contribution). Fix them before filing.",
  history:
    "Download History: every ESI file downloaded, with who and when. Re-download the exact same file any time.",
};

const EMPTY_FILTERS = {
  department_id: "",
  designation_id: "",
  location_id: "",
  employee_id: "",
  employee_status: "",
  esi_eligible: "",
  zero_contribution: "",
  contribution_period: "",
  insurance_number: "",
  cost_center: "",
  search: "",
};

const ESI_SHELL_REPORT = {
  key: "esi_monthly",
  name: "ESI Report",
  category: "Statutory Reports",
  icon: HeartPulse,
  description: "Enterprise ESIC monthly contribution report from processed payroll.",
  href: "/reports/esi",
};

function formatApiError(error, fallback = "Request failed") {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  return getApiErrorDetail(error) || toDisplayString(error?.message) || fallback;
}

function parseBlobError(blob) {
  return blob.text().then((text) => {
    try {
      const parsed = JSON.parse(text);
      return formatApiError({ response: { data: parsed } }, "Export failed");
    } catch {
      return "Export failed";
    }
  });
}

export default function EsiReportsWorkspace() {
  const { employees } = useEmployees();
  const [theme, setTheme] = useState("light");
  const nav = useReportsNavigation({ payrollReports: PAYROLL_HUB_REPORTS });

  const [mode, setMode] = useState("monthly");
  const [year, setYear] = useState(nowYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [financialYear, setFinancialYear] = useState(nowYear >= 4 ? nowYear : nowYear - 1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [printing, setPrinting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sort, setSort] = useState({ key: "employee_code", direction: "asc" });
  const [visibleCols, setVisibleCols] = useState(null);
  const [showColPicker, setShowColPicker] = useState(false);

  const [filterOpts, setFilterOpts] = useState({ departments: [], designations: [], locations: [] });
  const [tab, setTab] = useState("report");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    loadFilterOptions().then(setFilterOpts);
  }, []);

  const buildBody = useCallback(
    () => {
      const f = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v != null) f[k] = v;
      });
      return {
        mode,
        year: Number(year),
        month: Number(month),
        financial_year: Number(financialYear),
        filters: Object.keys(f).length ? f : undefined,
        sort: { key: sort.key, direction: sort.direction },
      };
    },
    [mode, year, month, financialYear, filters, sort]
  );

  const load = useCallback(
    async (goPage = 1) => {
      setLoading(true);
      setError("");
      try {
        const res = await previewEsiReport(buildBody(), { page: goPage, pageSize: PAGE_SIZE });
        setData(res);
        setPage(goPage);
      } catch (e) {
        setData(null);
        setError(formatApiError(e, "Failed to load ESI report"));
      } finally {
        setLoading(false);
      }
    },
    [buildBody]
  );

  const handleExport = async (fmt) => {
    setExporting(fmt);
    setError("");
    try {
      const res = await downloadEsiReport({ ...buildBody(), format: fmt });
      triggerBlobDownload(res, `esi_report.${fmt}`);
    } catch (e) {
      let msg = formatApiError(e, "Export failed");
      if (e?.response?.data instanceof Blob) {
        try {
          msg = await parseBlobError(e.response.data);
        } catch (_) {} // eslint-disable-line no-empty
      }
      setError(msg);
    } finally {
      setExporting(null);
    }
  };

  // Print every row for the period (not just the current 50-row page) as a clean table.
  const handlePrint = async () => {
    setPrinting(true);
    setError("");
    try {
      const res = await previewEsiReport(buildBody(), { page: 1, pageSize: 500 });
      const cols = (res?.columns || []).filter((c) => !visibleCols || visibleCols.has(c.key));
      const allRows = res?.rows || [];
      if (!allRows.length) {
        setError("No rows to print for this period.");
        return;
      }
      const h = res?.header || {};
      const labels = cols.map((c) => c.label || c.key);
      printRowsAsTable({
        title: "ESI Report",
        subtitle: [
          h.company_name,
          h.esic_employer_code ? `ESIC Code: ${h.esic_employer_code}` : null,
          h.wage_month ? `Wage Month: ${h.wage_month}` : null,
          `Employees: ${allRows.length}`,
        ]
          .filter(Boolean)
          .join("  |  "),
        columns: labels,
        rows: allRows.map((r) => Object.fromEntries(cols.map((c) => [c.label || c.key, cellDisplay(r, c)]))),
        keyColumns: labels.filter((l) => /^(employee code|employee name|insurance|ip)/i.test(l)).slice(0, 3),
      });
    } catch (e) {
      setError(formatApiError(e, "Print failed"));
    } finally {
      setPrinting(false);
    }
  };

  const resetFilters = () => {
    setMode("monthly");
    setYear(nowYear);
    setMonth(new Date().getMonth() + 1);
    setFinancialYear(nowYear >= 4 ? nowYear : nowYear - 1);
    setFilters(EMPTY_FILTERS);
    setData(null);
    setError("");
  };

  const loadHistory = useCallback(() => {
    getEsiHistory()
      .then((res) => setHistory(res.history || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "history") loadHistory();
  }, [tab, loadHistory]);

  const columns = useMemo(() => data?.columns || [], [data]);
  const activeColumns = useMemo(() => {
    if (!visibleCols) return columns;
    return columns.filter((c) => visibleCols.has(c.key));
  }, [columns, visibleCols]);

  useEffect(() => {
    if (columns.length && !visibleCols) {
      setVisibleCols(new Set(columns.map((c) => c.key)));
    }
  }, [columns, visibleCols]);

  const toggleSort = (key) => {
    if (!SORTABLE.has(key)) return;
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  useEffect(() => {
    if (data) load(1);
  }, [sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = data?.summary || {};
  const header = data?.header || {};
  const pagination = data?.pagination || {};
  const totals = data?.column_totals || {};
  const validations = data?.validations || [];
  const exceptions = data?.exceptions || {};
  const exceptionCount = Object.values(exceptions).reduce(
    (a, v) => a + (Array.isArray(v) ? v.length : 0),
    0
  );

  const employeeOptions = useMemo(
    () =>
      (employees || []).map((e) => ({
        id: e.employee_id || e.id,
        label: `${e.employee_code || ""} ${e.display_name || e.first_name || ""}`.trim(),
      })),
    [employees]
  );


  return (
    <div className="prw esi-rpt" data-theme={theme}>
      <ReportsEnterpriseShell
        categoryOrder={nav.categoryOrder}
        reportsByCategory={nav.reportsByCategory}
        selectedKey="esi_monthly"
        selectedReport={nav.selectedReport || ESI_SHELL_REPORT}
        reportSearch={nav.reportSearch}
        onReportSearchChange={nav.onReportSearchChange}
        favorites={nav.favorites}
        onToggleFavorite={nav.onToggleFavorite}
        recentReports={nav.recentReports}
        favReports={nav.favReports}
        onSelectReport={nav.onSelectReport}
        onThemeChange={setTheme}
        theme={theme}
        moduleTitle="ESI Reports"
      >
      <div className="prw__body esi-rpt__content">
        {/* Page header — same layout as the other report pages */}
        <div className="prw__page-header">
          <div className="prw__page-header-main">
            <div className="prw__page-icon">
              <HeartPulse size={26} />
            </div>
            <div>
              <h1 className="prw__page-title">ESI Report</h1>
              <p className="prw__page-desc">
                ESIC monthly contribution report from finalized payroll — statutory columns, filters, and exports.
              </p>
              {data && (
                <div className="prw__page-meta">
                  {header.company_name && (
                    <span className="prw__meta-chip"><Building2 size={13} /> <b>{header.company_name}</b></span>
                  )}
                  <span className="prw__meta-chip">ESIC Code: <b>{header.esic_employer_code || "—"}</b></span>
                  <span className="prw__meta-chip"><CalendarDays size={13} /> Wage Month: <b>{header.wage_month || header.payroll_month || "—"}</b></span>
                  <span className="prw__meta-chip">Financial Year: <b>{header.financial_year || "—"}</b></span>
                  <span className="prw__meta-chip">
                    <Users size={13} /> Generated by: <b>{header.generated_by || "—"}</b>
                    {header.report_generated_date ? ` · ${header.report_generated_date} ${header.report_generated_time || ""}` : ""}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filter card */}
        <div className="prw__filter-card">
          <p className="prw__filter-card-title">
            <Filter size={16} /> Report Filters
          </p>
          <div className="prw__filter-grid">
            <div className="prw__filter">
              <label htmlFor="esi-mode"><CalendarDays size={12} /> Period</label>
              <select id="esi-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="half_yearly">Half-Yearly</option>
                <option value="financial_year">Financial Year</option>
              </select>
            </div>
            {mode === "monthly" && (
              <>
                <div className="prw__filter">
                  <label htmlFor="esi-month"><CalendarDays size={12} /> Month</label>
                  <select id="esi-month" value={month} onChange={(e) => setMonth(e.target.value)}>
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div className="prw__filter">
                  <label htmlFor="esi-year"><CalendarDays size={12} /> Year</label>
                  <input id="esi-year" type="number" value={year} onChange={(e) => setYear(e.target.value)} />
                </div>
              </>
            )}
            {mode !== "monthly" && (
              <div className="prw__filter">
                <label htmlFor="esi-fy"><CalendarDays size={12} /> Financial Year (Start Year)</label>
                <input id="esi-fy" type="number" value={financialYear} onChange={(e) => setFinancialYear(e.target.value)} />
              </div>
            )}
            <div className="prw__filter">
              <label htmlFor="esi-dept"><Building2 size={12} /> Department</label>
              <select
                id="esi-dept"
                value={filters.department_id}
                onChange={(e) => setFilters({ ...filters, department_id: e.target.value })}
              >
                <option value="">All Departments</option>
                {filterOpts.departments.map((d) => (
                  <option key={d.id || d.department_id} value={d.id || d.department_id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="prw__filter">
              <label htmlFor="esi-search"><Search size={12} /> Search</label>
              <input
                id="esi-search"
                type="search"
                placeholder="IP number, code, name…"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && load(1)}
              />
            </div>

            {showAdvanced && (
              <>
                <div className="prw__filter">
                  <label htmlFor="esi-desig">Designation</label>
                  <select id="esi-desig" value={filters.designation_id} onChange={(e) => setFilters({ ...filters, designation_id: e.target.value })}>
                    <option value="">All Designations</option>
                    {filterOpts.designations.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div className="prw__filter">
                  <label htmlFor="esi-loc">Branch</label>
                  <select id="esi-loc" value={filters.location_id} onChange={(e) => setFilters({ ...filters, location_id: e.target.value })}>
                    <option value="">All Branches</option>
                    {filterOpts.locations.map((l) => (
                      <option key={l.id || l.location_id} value={l.id || l.location_id}>{l.name}</option>
                    ))}
                  </select>
                </div>
                <div className="prw__filter">
                  <label htmlFor="esi-emp">Employee</label>
                  <select id="esi-emp" value={filters.employee_id} onChange={(e) => setFilters({ ...filters, employee_id: e.target.value })}>
                    <option value="">All Employees</option>
                    {employeeOptions.map((e) => (
                      <option key={e.id} value={e.id}>{e.label}</option>
                    ))}
                  </select>
                </div>
                <div className="prw__filter">
                  <label htmlFor="esi-status">Status</label>
                  <select id="esi-status" value={filters.employee_status} onChange={(e) => setFilters({ ...filters, employee_status: e.target.value })}>
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="prw__filter">
                  <label htmlFor="esi-elig">ESI Eligible</label>
                  <select id="esi-elig" value={filters.esi_eligible} onChange={(e) => setFilters({ ...filters, esi_eligible: e.target.value })}>
                    <option value="">All</option>
                    <option value="true">Eligible only</option>
                    <option value="false">Excluded only</option>
                  </select>
                </div>
                <div className="prw__filter">
                  <label htmlFor="esi-zero">Zero Contribution</label>
                  <select id="esi-zero" value={filters.zero_contribution} onChange={(e) => setFilters({ ...filters, zero_contribution: e.target.value })}>
                    <option value="">All</option>
                    <option value="true">Zero only</option>
                    <option value="false">With contribution</option>
                  </select>
                </div>
                <div className="prw__filter">
                  <label htmlFor="esi-cp">Contribution Period</label>
                  <select id="esi-cp" value={filters.contribution_period} onChange={(e) => setFilters({ ...filters, contribution_period: e.target.value })}>
                    <option value="">All</option>
                    <option value="Apr-Sep">Apr–Sep</option>
                    <option value="Oct-Mar">Oct–Mar</option>
                  </select>
                </div>
                <div className="prw__filter">
                  <label htmlFor="esi-ip">Insurance Number</label>
                  <input id="esi-ip" value={filters.insurance_number} onChange={(e) => setFilters({ ...filters, insurance_number: e.target.value })} />
                </div>
                <div className="prw__filter">
                  <label htmlFor="esi-cc">Cost Center</label>
                  <input id="esi-cc" value={filters.cost_center} onChange={(e) => setFilters({ ...filters, cost_center: e.target.value })} />
                </div>
              </>
            )}

            <div className="prw__filter-actions">
              <button
                type="button"
                className="prw__btn prw__btn--generate"
                onClick={() => load(1)}
                disabled={loading}
                aria-busy={loading}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                Generate
              </button>
              <button type="button" className="prw__btn prw__btn--ghost" onClick={resetFilters}>
                <RotateCcw size={16} /> Reset
              </button>
              <button
                type="button"
                className="prw__btn prw__btn--ghost"
                onClick={() => setShowAdvanced((v) => !v)}
                aria-expanded={showAdvanced}
              >
                <Filter size={16} /> {showAdvanced ? "Fewer Filters" : "More Filters"}
                {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
          </div>
        </div>

        {/* Action toolbar */}
        <div className="prw__toolbar">
          <button
            type="button"
            className="prw__btn prw__btn--refresh prw__btn--sm"
            onClick={() => load(page)}
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button type="button" className="prw__btn prw__btn--excel prw__btn--sm" onClick={() => handleExport("xlsx")} disabled={Boolean(exporting)}>
            {exporting === "xlsx" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />} Excel
          </button>
          <button type="button" className="prw__btn prw__btn--pdf prw__btn--sm" onClick={() => handleExport("pdf")} disabled={Boolean(exporting)}>
            {exporting === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} PDF
          </button>
          <button type="button" className="prw__btn prw__btn--csv prw__btn--sm" onClick={() => handleExport("csv")} disabled={Boolean(exporting)}>
            {exporting === "csv" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} CSV
          </button>
          <button type="button" className="prw__btn prw__btn--print prw__btn--sm" onClick={handlePrint} disabled={printing}>
            {printing ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />} Print
          </button>
        </div>

        {data && (
          <div className="esi-rpt__cards">
            {SUMMARY_CARDS.map((card) => (
              <div key={card.key} className={`esi-rpt__card${card.tone ? ` esi-rpt__card--${card.tone}` : ""}`}>
                <p className="esi-rpt__card-label">{card.label}</p>
                <p className="esi-rpt__card-value">
                  {card.money ? formatMoney(summary[card.key]) : (summary[card.key] ?? "—")}
                </p>
              </div>
            ))}
          </div>
        )}

      {error && (
        <div className="esi-rpt__error" role="alert">
          <AlertTriangle size={16} /> {toDisplayString(error, "Something went wrong")}
        </div>
      )}

      <nav className="esi-rpt__tabs">
        {[
          ["report", "Report", Table2],
          ["validations", "Validations", AlertTriangle, validations.length],
          ["history", "Download History", History],
        ].map(([key, label, Icon, badge]) => (
          <button
            key={key}
            type="button"
            className={`esi-rpt__tab${tab === key ? " is-active" : ""}`}
            onClick={() => setTab(key)}
          >
            <Icon size={14} /> {label}
            {badge > 0 && <span style={{ background: "#dc2626", color: "#fff", borderRadius: 10, padding: "0 6px", fontSize: 10 }}>{badge}</span>}
            {key === "validations" && exceptionCount > 0 && validations.length === 0 && (
              <span style={{ background: "#d97706", color: "#fff", borderRadius: 10, padding: "0 6px", fontSize: 10 }}>{exceptionCount}</span>
            )}
          </button>
        ))}
      </nav>
      <p className="esi-rpt__tab-info">
        <Info size={14} /> {TAB_INFO[tab]}
      </p>

      <div className="esi-rpt__body">
        {tab === "report" && (
          <>
            {loading && !data && (
              <div className="esi-rpt__skeleton">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="esi-rpt__skeleton-line" style={{ width: `${60 + (i % 3) * 15}%` }} />
                ))}
              </div>
            )}
            {!loading && !data && (
              <div className="esi-rpt__empty">
                <Table2 size={48} />
                <p>Select period and click <b>Generate</b> to load the ESI report from processed payroll.</p>
              </div>
            )}
            {data && (
              <>
                <div className="esi-rpt__col-panel">
                  <button type="button" className="esi-rpt__btn" onClick={() => setShowColPicker((v) => !v)}>
                    <Columns3 size={14} /> Columns
                  </button>
                  {showColPicker &&
                    columns.map((c) => (
                      <label key={c.key} className="esi-rpt__col-chip">
                        <input
                          type="checkbox"
                          checked={visibleCols?.has(c.key)}
                          onChange={() => {
                            setVisibleCols((prev) => {
                              const next = new Set(prev);
                              if (next.has(c.key)) next.delete(c.key);
                              else next.add(c.key);
                              return next;
                            });
                          }}
                        />
                        {c.label}
                      </label>
                    ))}
                </div>
                <div className="esi-rpt__table-wrap">
                  <table className="esi-rpt__table">
                    <thead>
                      <tr>
                        {activeColumns.map((c) => (
                          <th
                            key={c.key}
                            className={c.type === "money" || c.type === "number" ? "num" : ""}
                            onClick={() => toggleSort(c.key)}
                          >
                            {c.label}
                            {SORTABLE.has(c.key) && sort.key === c.key && (
                              sort.direction === "asc" ? " ↑" : " ↓"
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(data.rows || []).map((row, i) => (
                        <tr key={row.employee_id || i}>
                          {activeColumns.map((c) => (
                            <td key={c.key} className={c.type === "money" || c.type === "number" ? "num" : ""}>
                              {cellDisplay(row, c)}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {(data.rows || []).length === 0 && (
                        <tr>
                          <td colSpan={activeColumns.length} style={{ textAlign: "center", padding: 24 }}>
                            No employees match the selected filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {Object.keys(totals).length > 0 && (
                      <tfoot>
                        <tr>
                          {activeColumns.map((c, idx) => (
                            <td key={c.key} className={c.type === "money" || c.type === "number" ? "num" : ""}>
                              {idx === 0 ? "Totals" : totals[c.key] != null ? (c.type === "money" ? formatMoney(totals[c.key]) : totals[c.key]) : ""}
                            </td>
                          ))}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                <div className="esi-rpt__footer-bar">
                  <span>
                    Page {pagination.page || 1} / {pagination.total_pages || 1} · {pagination.total_rows || 0} employees
                  </span>
                  <span>
                    <button type="button" className="esi-rpt__btn" disabled={page <= 1 || loading} onClick={() => load(page - 1)}>Prev</button>
                    {" "}
                    <button type="button" className="esi-rpt__btn" disabled={page >= (pagination.total_pages || 1) || loading} onClick={() => load(page + 1)}>Next</button>
                  </span>
                </div>
              </>
            )}
          </>
        )}

        {tab === "validations" && (
          <div>
            {!data && <div className="esi-rpt__empty"><p>Generate a report to view validations.</p></div>}
            {data && validations.length === 0 && exceptionCount === 0 && (
              <div className="esi-rpt__empty" style={{ color: "#059669" }}>
                <ShieldCheck size={32} /> No validation issues detected.
              </div>
            )}
            {validations.map((v, i) => (
              <div key={i} className="esi-rpt__validation">
                <strong>{v.type?.replace(/_/g, " ")}</strong>
                {v.employee_code && ` · ${v.employee_code}`}
                {v.employee_name && ` · ${v.employee_name}`}
                {v.message && <div>{toDisplayString(v.message)}</div>}
              </div>
            ))}
            {Object.entries(exceptions).filter(([, items]) => Array.isArray(items) && items.length > 0).map(([kind, items]) => (
              <div key={kind} className="esi-rpt__validation">
                <strong>{kind.replace(/_/g, " ")} ({items.length})</strong>
                <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                  {items.slice(0, 30).map((it, i) => (
                    <li key={i}>
                      {Object.entries(it)
                        .map(([k, val]) => `${k}: ${toDisplayString(val)}`)
                        .join(" · ")}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {tab === "history" && (
          <div className="esi-rpt__table-wrap">
            <table className="esi-rpt__table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Format</th>
                  <th className="num">Size</th>
                  <th>By</th>
                  <th>When</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.artifact_id}>
                    <td>{h.period_label}</td>
                    <td>{(h.fmt || "").toUpperCase()}</td>
                    <td className="num">{Math.round((h.byte_size || 0) / 1024)} KB</td>
                    <td>{h.generated_by_label || "—"}</td>
                    <td>{h.created_at ? new Date(h.created_at).toLocaleString() : ""}</td>
                    <td>
                      <button
                        type="button"
                        className="esi-rpt__btn"
                        onClick={async () => {
                          const res = await downloadArtifact(h.artifact_id);
                          triggerBlobDownload(res, h.file_name);
                        }}
                      >
                        <Download size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: "center", padding: 24 }}>No download history yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </ReportsEnterpriseShell>
    </div>
  );
}
